import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { environment } from '@environments/environment';
import { CrewListResponse } from './models/response/crew-list.response';
import { CrewRequest } from './models/request/crew.request';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { ApiCacheService } from '@shared/services/api-cache.service';
import { DecsResponse } from '@shared/services/decs/models/response/decs.response';
import { DecsRequest } from '@shared/services/decs/models/request/decs.request';
import { DecsService } from '@shared/services/decs/decs.service';
import { DateTime } from 'luxon';

@Injectable({
  providedIn: 'root',
})
export class CrewService {
  private readonly _url = `${environment.apiUrl}crew`;

  constructor(
    private cacheService: ApiCacheService,
    private decsService: DecsService
  ) {}

  /** Get crew list using standard cache strategy. */
  getCrewList(
    crewRequest: CrewRequest,
    isRefresh = false
  ): Observable<RequestState<CrewListResponse>> {
    if (
      crewRequest.flightNumber === '0000' ||
      crewRequest.date === '' ||
      crewRequest.depStation === ''
    ) {
      return of(
        new RequestState<CrewListResponse>(RequestStatus.IsCompleteNoResponse)
      );
    }

    const cacheId = this.getCacheId(crewRequest);
    const url = `${this._url}/${crewRequest.flightNumber}/${crewRequest.date}/${crewRequest.depStation}`;

    if (isRefresh) {
      return this.cacheService.forceRefreshGet<CrewListResponse>(cacheId, url);
    }

    return this.cacheService.request<CrewListResponse>(cacheId, url).pipe(
      map((crewResponse) => {
        if (crewResponse.response) {
          // Inject request into response. This is to help identify sequence dates from the request.
          crewResponse.response.request = crewRequest;
        }
        return crewResponse;
      })
    );
  }

  private getCacheId(crewRequest: CrewRequest): string {
    return `crew-${crewRequest.flightNumber}/${crewRequest.date}/${crewRequest.depStation}`;
  }

  getCrewLocation(
    crewRequest: CrewRequest,
    isRefresh = false
  ): Observable<RequestState<DecsResponse>> {
    const sabreDate = DateTime.fromFormat(
      crewRequest.date,
      'yyyy-MM-dd'
    ).toFormat('ddMMM');

    const decsRequest: DecsRequest = {
      cmd: `NSG/${crewRequest.flightNumber}/${sabreDate}/${crewRequest.depStation}`,
    };

    return this.decsService.submitDecsRequest(decsRequest, isRefresh);
  }
}
